import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { WarningIcon, SpinnerIcon } from '@phosphor-icons/react';
import {
  useCreateProductGroup,
  useUpdateProductGroup,
  ProductGroup,
} from 'src/api/rebates/useProductGroups';
import { ALL_OTHER_PRODUCTS, NON_REBATABLE_PRODUCTS } from '@erp/shared-utils';
import { T, Var, useGT } from 'gt-react';

/** Parse pipe-separated names into trimmed, non-empty values */
function parsePipeSeparatedNames(input: string): string[] {
  return input
    .split('|')
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
}

interface Props {
  open: boolean;
  onClose: () => void;
  supplierId: string;
  productGroup?: ProductGroup; // If provided, we're editing; if not, we're creating
  onSuccess?: () => void;
}

const WARNING_NAMES = [
  'all',
  'other',
  'various',
  'nonrebatable',
  'non rebateable',
  'non-rebatable',
  'misc',
  'general',
  'default',
];

const ProductGroupModalShadcn: React.FC<Props> = ({
  open,
  onClose,
  supplierId,
  productGroup,
  onSuccess,
}) => {
  const gt = useGT();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isHistorical: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const createMutation = useCreateProductGroup();
  const updateMutation = useUpdateProductGroup(productGroup?.id || '');

  const isEditing = !!productGroup;
  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const isCatchAll = productGroup?.isCatchAll ?? false;

  // Parse names when creating (supports pipe-separated values)
  const parsedNames = useMemo(() => {
    if (isEditing) {
      return [formData.name.trim()];
    }
    return parsePipeSeparatedNames(formData.name);
  }, [formData.name, isEditing]);

  const isMultipleGroups = !isEditing && parsedNames.length > 1;

  // Only validate against reserved names if NOT a catch-all group
  const isInvalidName =
    !isCatchAll &&
    parsedNames.some(
      (name) =>
        name.toLowerCase() === ALL_OTHER_PRODUCTS.toLowerCase() ||
        name.toLowerCase() === NON_REBATABLE_PRODUCTS.toLowerCase(),
    );

  // Only show warning if NOT a catch-all group
  const isWarningName =
    !isCatchAll &&
    parsedNames.some((name) =>
      WARNING_NAMES.some((warningName) =>
        name.toLowerCase().includes(warningName.toLowerCase()),
      ),
    );

  // Reset form when modal opens/closes or productGroup changes
  useEffect(() => {
    if (open) {
      if (productGroup) {
        setFormData({
          name: productGroup.name,
          description: productGroup.description || '',
          // Catch-all groups cannot be historical
          isHistorical: productGroup.isCatchAll
            ? false
            : productGroup.isHistorical || false,
        });
      } else {
        setFormData({
          name: '',
          description: '',
          isHistorical: false,
        });
      }
      setErrors({});
    }
  }, [open, productGroup]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (parsedNames.length === 0) {
      newErrors.name = gt('Name is required', {
        context: 'product group form validation',
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          name: formData.name.trim(),
          description: formData.description.trim(),
          // Catch-all groups cannot be historical
          isHistorical: isCatchAll ? false : formData.isHistorical,
        });
      } else {
        await createMutation.mutateAsync({
          supplierId: supplierId,
          name: formData.name.trim(),
          description: formData.description.trim(),
          // Catch-all groups cannot be historical
          isHistorical: isCatchAll ? false : formData.isHistorical,
        });
      }

      // Success
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving product group:', error);
    }
  };

  const handleInputChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: '',
        }));
      }
    };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? (
              <T context="product group modal title edit">Edit Product Group</T>
            ) : (
              <T context="product group modal title create">
                Create Product Group
              </T>
            )}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? (
              <T context="product group modal description edit">
                Update the product group information below.
              </T>
            ) : (
              <T context="product group modal description create">
                Create a new product group for this supplier.
              </T>
            )}
          </DialogDescription>
        </DialogHeader>

        {isCatchAll && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-sm">
            <Badge variant="secondary" className="shrink-0 mt-0.5">
              <T context="catch-all product group badge">Default Group</T>
            </Badge>
            <div className="flex-1">
              <p className="font-medium mb-1">
                <T context="catch-all product group heading">
                  Special Product Group
                </T>
              </p>
              <p className="text-xs text-blue-800">
                <T context="explanation for default catch-all product group">
                  This is the default catch-all product group. Items not
                  assigned to specific product groups will be assigned here.
                  This group cannot be deleted or marked as historical.
                </T>
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">
                {isEditing ? (
                  <T context="product group name field label singular">Name</T>
                ) : (
                  <T context="product group name field label plural">Name(s)</T>
                )}{' '}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={handleInputChange('name')}
                placeholder={
                  isEditing
                    ? gt('Enter product group name', {
                        context: 'placeholder single product group name',
                      })
                    : gt('Enter names (use | to separate multiple)', {
                        context: 'placeholder multiple product group names',
                      })
                }
                disabled={isLoading}
                className={errors.name ? 'border-red-500' : ''}
              />
              {!isEditing && (
                <p className="text-xs text-muted-foreground">
                  <T context="tip for pipe-separated product group names">
                    Tip: Use pipe (|) to create multiple groups at once, e.g.
                    &quot;Apples | Oranges | Bananas&quot;
                  </T>
                </p>
              )}
              {errors.name && (
                <span className="text-sm text-red-500">{errors.name}</span>
              )}
              {isMultipleGroups && (
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className="text-xs text-muted-foreground mr-1">
                    <T>
                      Will create <Var>{parsedNames.length}</Var> groups:
                    </T>
                  </span>
                  {parsedNames.map((name, idx) => (
                    <Badge
                      key={idx}
                      variant="secondary"
                      className="text-xs py-0"
                    >
                      {name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">
                <T context="product group description field label">
                  Description
                </T>
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder={gt('Enter product group description (optional)', {
                  context: 'placeholder product group description',
                })}
                disabled={isLoading}
                rows={3}
              />
            </div>

            {!isCatchAll && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="isHistorical">
                      <T context="historical product group toggle label">
                        Historical
                      </T>
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      <T context="help text for historical product group toggle">
                        Mark this product group as historical (excluded from new
                        processing)
                      </T>
                    </p>
                  </div>
                  <Switch
                    id="isHistorical"
                    checked={formData.isHistorical}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        isHistorical: checked,
                      }))
                    }
                    disabled={isLoading}
                  />
                </div>

                {formData.isHistorical && (
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className="bg-gray-100 text-gray-800"
                    >
                      <T context="badge for historical product group">
                        Historical
                      </T>
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      <T context="note when product group marked historical">
                        This product group will be excluded from new rebate
                        processing
                      </T>
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {isInvalidName && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm mb-4">
              <WarningIcon className="h-4 w-4 text-yellow-600 flex-shrink-0" />
              <span>
                <T>
                  <strong>
                    <Var>{formData.name}</Var>
                  </strong>{' '}
                  is not a valid product group name.
                </T>
              </span>
            </div>
          )}
          {isWarningName && !isInvalidName && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm mb-4">
              <WarningIcon className="h-4 w-4 text-yellow-600 flex-shrink-0" />
              <span>
                <T>
                  <strong>
                    <Var>{formData.name}</Var>
                  </strong>{' '}
                  might be captured by the default product group or non
                  rebatable product group.
                </T>
              </span>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              <T context="cancel product group modal">Cancel</T>
            </Button>
            <Button type="submit" disabled={isLoading || isInvalidName}>
              {isLoading && (
                <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEditing ? (
                <T context="submit product group edit">Update</T>
              ) : isMultipleGroups ? (
                <T>
                  Create <Var>{parsedNames.length}</Var> Groups
                </T>
              ) : (
                <T context="submit create product group">Create</T>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductGroupModalShadcn;
